import { useLiveQuery } from "dexie-react-hooks";
import { ChevronLeft, TrendingDown } from "lucide-react";
import { bestStore, CATEGORY_COLORS, CATEGORY_LABELS, de, priceSeries } from "@ainkauf/core";
import { BottomNav } from "@/components/BottomNav";
import { LINIENFARBEN, PriceChart } from "@/components/PriceChart";
import { allStores, priceEntriesOfProduct, productById } from "@/db/repository";
import { kategorieZeichen } from "@/lib/category-icons";
import { useNavigate } from "@/lib/router";

const euro = (cents: number) => (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

/**
 * Ein Produkt und was es bisher gekostet hat.
 *
 * Die Frage, die dieser Bildschirm beantwortet, ist nicht "was kostet das",
 * sondern "wo kaufe ich es besser". Deshalb steht der Vergleich zwischen den
 * Läden oben und der Verlauf darunter.
 */
export function ProductScreen({ productId }: { productId: string }) {
  const go = useNavigate();

  const product = useLiveQuery(() => productById(productId), [productId], null);
  const entries = useLiveQuery(() => priceEntriesOfProduct(productId), [productId], []);
  const stores = useLiveQuery(() => allStores(), [], []);

  const reihen = priceSeries(entries, productId);
  const bester = bestStore(entries, productId);

  const ladenName = (id: string | null) => stores.find((store) => store.id === id)?.name ?? de.prices.noStore;
  const farbe = product?.category ? `var(--food-${CATEGORY_COLORS[product.category]})` : "var(--ink-3)";

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-2xl flex-col gap-5 px-4 mit-leiste">
      <header className="flex items-center gap-3 pt-4">
        <button
          type="button"
          onClick={() => {
            go("/");
          }}
          aria-label={de.common.back}
          data-testid="product-back"
          className="inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-card text-ink-2 shadow-[var(--shadow-card)]"
        >
          <ChevronLeft size={22} strokeWidth={2} aria-hidden />
        </button>
        <h1 className="min-w-0 flex-1 truncate text-[1.75rem]" data-testid="product-name">
          {product?.name ?? ""}
        </h1>
      </header>

      <section
        className="auftritt relative flex items-center gap-3 overflow-hidden rounded-[var(--radius-xl)] px-4 py-4"
        style={{
          background: `linear-gradient(135deg, #fff 0%, color-mix(in oklab, ${farbe} 14%, #fff) 100%)`,
          boxShadow: "var(--shadow-card)",
        }}
      >
        <span
          aria-hidden
          className="inline-grid size-12 shrink-0 place-items-center rounded-full"
          style={{ background: `color-mix(in oklab, ${farbe} 20%, #fff)`, color: farbe }}
        >
          {kategorieZeichen(product?.category ?? null, 24)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-bold uppercase tracking-[0.08em]" style={{ color: "var(--ink-2)" }}>
            {product?.category ? CATEGORY_LABELS[product.category] : de.items.withoutCategory}
          </p>
          <p className="num text-sm text-ink-2">
            {product && product.last_price_cents !== null ? de.prices.lastPaid(euro(product.last_price_cents)) : de.prices.empty}
            {product ? ` · ${de.prices.boughtCount(product.use_count)}` : ""}
          </p>
        </div>
      </section>

      {/* Der eine Satz, wegen dem man hier ist. */}
      {bester !== null && (
        <p
          className="flex items-center gap-2 rounded-[var(--radius-lg)] bg-herb-soft px-4 py-3 text-sm font-semibold text-herb"
          data-testid="product-best-store"
        >
          <TrendingDown size={18} strokeWidth={2.25} className="shrink-0" aria-hidden />
          {de.prices.cheapest(ladenName(bester.storeId), bester.savingsPercent)}
        </p>
      )}

      <section className="flex flex-col gap-3">
        <h2 className="text-lg">{de.prices.title}</h2>

        {reihen.length === 0 ? (
          <div className="karte flex flex-col gap-1 px-5 py-8 text-center" data-testid="product-no-prices">
            <p className="font-semibold text-ink">{de.prices.empty}</p>
            <p className="text-sm text-ink-2">{de.prices.emptyHint}</p>
          </div>
        ) : (
          <>
            <PriceChart series={reihen} label={ladenName} />

            <ul className="flex flex-col gap-2" data-testid="product-series">
              {reihen.map((reihe, i) => (
                <li
                  key={reihe.storeId ?? "ohne"}
                  className="karte kante auftritt flex min-h-[54px] items-center justify-between gap-3 px-4"
                  style={{
                    ["--kante-farbe" as string]: LINIENFARBEN[i % LINIENFARBEN.length],
                    ["--takt" as string]: `${String(i * 40)}ms`,
                  }}
                >
                  <span className="min-w-0 truncate font-medium">{ladenName(reihe.storeId)}</span>
                  <span className="num shrink-0 text-sm text-ink-2">
                    {euro(reihe.latestCents)}
                    <span className="ml-2 text-ink-3">{de.prices.observations(reihe.points.length)}</span>
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <BottomNav active="listen" />
    </div>
  );
}
